import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import emailjs from "@emailjs/browser";

// --- CSS Imports ---
import "../Styling/Home_New.css";

// --- Asset Imports ---
import logo from "../Assets/logo.jpeg";
import Banner1 from "../Assets/Home_slide_1.png";
import Banner2 from "../Assets/Home_slide_2.png";
import Banner3 from "../Assets/Home_slide_3.png";
import Banner4 from "../Assets/Home_slide_4.png";
import Banner5 from "../Assets/Home_slide_5.png";
import MobileBanner1 from "../Assets/Home_slide_1.png";
import MobileBanner2 from "../Assets/Home_slide_2.png";
import MobileBanner3 from "../Assets/Home_slide_3.png";
import MobileBanner4 from "../Assets/Home_slide_4.png";
import MobileBanner5 from "../Assets/Home_slide_5.png";

// --- Icon Imports ---
import {
    ChevronDown,
    Menu,
    X,
    Truck,
    PhoneCall,
    FileText,
    Sparkles,
    Code,
    Settings,
    TrendingUp,
    Target,
    Lightbulb,
    Search,
    Monitor,
    Zap,
    Check,
    Star,
    Quote,
    ChevronLeft,
    ChevronRight,
    Mail,
    Phone,
    MapPin,
    ArrowRight,
    Send,
    Linkedin,
    Instagram,
    Cpu,
    Shield,
    Database,
    Infinity,
    BarChart3,
    Rocket
} from "lucide-react";

// --- External Component Imports (Source not provided in prompt) ---
import ChatbotWidget from "./ChatComponent";
import BackgroundAnimation from "../Components/BackgroundAnimation";

// Register GSAP Plugin
gsap.registerPlugin(ScrollTrigger);

// ==========================================
// SUB-COMPONENTS
// ==========================================

function Navbar() {
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <nav className="CN-navbar">
            <div className="container">
                <div className="d-flex justify-content-between align-items-center">
                    {/* Brand */}
                    <Link to="/" className="CN-brand">
                        <img
                            src={logo}
                            alt="Digital Dream Works Logo"
                            style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "contain",
                            }}
                        />
                        <span>Digital Dream Works</span>
                    </Link>

                    {/* Nav Links */}
                    <ul className={`CN-nav-links ${menuOpen ? "active" : ""}`}>
                        <li className="CN-nav-item">
                            <a href="#!" className="CN-nav-link CN-nav-link-bordered">
                                Services <ChevronDown size={14} className="CN-dropdown-icon" />
                            </a>
                            <div className="CN-dropdown-menu">
                                <Link to="/AI_Automation" className="CN-dropdown-item">
                                    AI Systems & Automation
                                </Link>
                                <Link to="/CustomSoftware" className="CN-dropdown-item">
                                    Custom Software Engineering
                                </Link>
                                <Link to="/MarketingSystem" className="CN-dropdown-item">
                                    Marketing & Growth Systems
                                </Link>
                                <Link to="/Web&Conversion" className="CN-dropdown-item">
                                    Web & Conversion Architecture
                                </Link>
                            </div>
                        </li>

                        <li className="CN-nav-item">
                            <Link to="/Labs" className="CN-nav-link">
                                Labs
                            </Link>
                        </li>
                        <li className="CN-nav-item">
                            <Link to="/How_we_work" className="CN-nav-link">
                                How We Work
                            </Link>
                        </li>
                        <li className="CN-nav-item">
                            <Link to="/PartnerShips" className="CN-nav-link">
                                Partnerships
                            </Link>
                        </li>
                        <li className="CN-nav-item">
                            <Link to="/AboutUs" className="CN-nav-link">
                                About
                            </Link>
                        </li>
                    </ul>

                    {/* CTA */}
                    <a
                        href="https://portfolio.DigitalDreamWorksAgency.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="CN-cta-button"
                    >
                        Portfolio
                    </a>

                    {/* Mobile Toggle */}
                    <button
                        className="CN-mobile-toggle"
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        {menuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>
        </nav>
    );
}

function Hero() {
    const [currentHeroSlide, setCurrentHeroSlide] = React.useState(0);
    const slidesCount = 5;
    const slideRefs = React.useRef([]);

    const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto slide every 6 seconds
    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrentHeroSlide((prev) => (prev + 1) % slidesCount);
        }, 6000);
        return () => clearInterval(interval);
    }, []);

    // Professional GSAP entrance for each slide's content
    React.useEffect(() => {
        const activeSlide = slideRefs.current[currentHeroSlide];
        if (activeSlide) {
            const heading = activeSlide.querySelector(".LD-hero-heading");
            const subheading = activeSlide.querySelector(".LD-hero-subheading");
            const buttons = activeSlide.querySelector(".LD-hero-buttons");

            // Make all hero content visible instantly
            gsap.set([heading, subheading, buttons], { y: 0, opacity: 1 });
        }
    }, [currentHeroSlide, isMobile]);

    const nextHeroSlide = () => {
        setCurrentHeroSlide((prev) => (prev + 1) % slidesCount);
    };

    const prevHeroSlide = () => {
        setCurrentHeroSlide((prev) => (prev === 0 ? slidesCount - 1 : prev - 1));
    };

    const goToHeroSlide = (index) => {
        setCurrentHeroSlide(index);
    };

    const desktopImages = [Banner1, Banner2, Banner3, Banner4, Banner5];
    const mobileImages = [
        MobileBanner1,
        MobileBanner2,
        MobileBanner3,
        MobileBanner4,
        MobileBanner5,
    ];

    const heroContent = [
        {
            heading: "Systems Engineering Built for Performance Operators",
            subheading: "Digital Dream Works designs and deploys production-ready AI systems across automation, software, and growth for teams that need leverage, not noise."
        },
        {
            heading: "Creative Automation Systems Built to Scale",
            subheading: "AI-powered design and creative automation systems built for performance-driven teams ready to dominate their vertical."
        },
        {
            heading: "Autonomous Systems for Revenue & Flow Control",
            subheading: "Enterprise-grade AI systems that assist teams with proposals, responses, and execution without replacing human command."
        },
        {
            heading: "AI Interfaces Integrated Directly Into Operations",
            subheading: "Voice and conversational systems integrated into real workflows, not standalone chat tools, providing seamless human-machine synergy."
        },
        {
            heading: "Internal Platforms That Run Real-World Logistics",
            subheading: "Mission-critical platforms designed to replace manual processes across logistics, workforce management, and daily operations."
        }
    ];

    return (
        <div className="LD-hero-carousel">
            {desktopImages.map((image, index) => (
                <div
                    key={index}
                    ref={(el) => (slideRefs.current[index] = el)}
                    className={`LD-hero-slide ${currentHeroSlide === index ? "LD-active" : ""}`}
                >
                    <picture>
                        <source
                            media="(max-width: 768px)"
                            srcSet={mobileImages[index]}
                        />
                        <img
                            src={image}
                            alt={`Slide ${index + 1}`}
                            className="LD-hero-image"
                        />
                    </picture>

                    {/* Professional Overlay */}
                    <div className="LD-hero-overlay"></div>

                    {/* Centered Content Wrapper */}
                    <div className="LD-hero-content-wrapper">
                        <div className="LD-hero-text-content">
                            <h1 className="LD-hero-heading">{heroContent[index].heading}</h1>
                            <p className="LD-hero-subheading">{heroContent[index].subheading}</p>
                        </div>

                        <div className="LD-hero-buttons">
                            <a
                                href="#labs"
                                className="LD-btn-primary"
                            >
                                Explore Our Systems →
                            </a>
                            <a
                                href="#services"
                                className="LD-btn-view-services"
                            >
                                View Services
                            </a>
                        </div>
                    </div>
                </div>
            ))}

            {/* Navigation Controls */}
            <button className="LD-carousel-nav LD-prev" onClick={prevHeroSlide} aria-label="Previous slide">
                ‹
            </button>
            <button className="LD-carousel-nav LD-next" onClick={nextHeroSlide} aria-label="Next slide">
                ›
            </button>

            {/* Slide Indicators */}
            <div className="LD-carousel-indicators">
                {[...Array(slidesCount)].map((_, index) => (
                    <span
                        key={index}
                        className={`LD-indicator ${currentHeroSlide === index ? "LD-active" : ""}`}
                        onClick={() => goToHeroSlide(index)}
                    />
                ))}
            </div>
        </div>
    );
}

function Intro() {
    const sectionRef = React.useRef(null);
    const svgRef = React.useRef(null);
    const containerRef = React.useRef(null);

    const services = React.useMemo(() => [
        { id: 1, name: "Truck Dispatching", icon: <Truck size={32} />, x: 200, y: 50 },
        { id: 2, name: "Custom Tech", icon: <Code size={32} />, x: 343, y: 154 },
        { id: 3, name: "Voice Assistants", icon: <PhoneCall size={32} />, x: 288, y: 321 },
        { id: 4, name: "Proposal Engine", icon: <FileText size={32} />, x: 112, y: 321 },
        { id: 5, name: "AI Designer", icon: <Sparkles size={32} />, x: 57, y: 154 },
    ], []);

    React.useEffect(() => {
        const ctx = gsap.context(() => {

            // Make ecosystem nodes and lines visible instantly
            gsap.set(".LD-center-hub", { scale: 1, opacity: 1 });
            services.forEach((service) => {
                gsap.set(`.node-${service.id}, .line-to-center-${service.id}, .line-outer-${service.id}`, {
                    scale: 1,
                    opacity: 1,
                    strokeDashoffset: 0
                });
            });
            gsap.set(".LD-cross-line", { strokeDashoffset: 0, opacity: 1 });
            ScrollTrigger.refresh();
        }, sectionRef);

        const refreshTimer = setTimeout(() => {
            ScrollTrigger.refresh();
        }, 500);

        return () => {
            ctx.revert();
            clearTimeout(refreshTimer);
        };
    }, [services]);

    return (
        <section className="CompS-section" ref={sectionRef}>
            <div className="container">
                <div className="CompS-bg-icon">
                    <Infinity size={400} />
                </div>
                <div className="CompS-header centered-header">
                    <span className="CompS-badge">Comprehensive Services</span>
                    <h2 className="CompS-title">Interconnected Solutions Ecosystem</h2>
                    <p className="CompS-desc">
                        We integrate cutting-edge technology pillars to build a cohesive digital foundation
                        that drives efficiency, scalability, and innovation for your enterprise.
                    </p>
                </div>

                <div className="LD-pentagon-wrapper" ref={containerRef}>
                    <div className="LD-pentagon-container">
                        <svg className="LD-pentagon-svg" viewBox="0 0 400 400" ref={svgRef}>
                            <defs>
                                <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8" />
                                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.8" />
                                </linearGradient>
                                <filter id="glow-effect" x="-20%" y="-20%" width="140%" height="140%">
                                    <feGaussianBlur stdDeviation="3" result="blur" />
                                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                </filter>
                            </defs>

                            {/* Cross Connections (Interconnectivity) */}
                            <line className="LD-cross-line" x1="200" y1="50" x2="288" y2="321" strokeDasharray="1000" strokeDashoffset="0" />
                            <line className="LD-cross-line" x1="200" y1="50" x2="112" y2="321" strokeDasharray="1000" strokeDashoffset="0" />
                            <line className="LD-cross-line" x1="343" y1="154" x2="112" y2="321" strokeDasharray="1000" strokeDashoffset="0" />
                            <line className="LD-cross-line" x1="343" y1="154" x2="57" y2="154" strokeDasharray="1000" strokeDashoffset="0" />
                            <line className="LD-cross-line" x1="288" y1="321" x2="57" y2="154" strokeDasharray="1000" strokeDashoffset="0" />

                            {/* Sequential Lines */}
                            {services.map((s, i) => {
                                const next = services[(i + 1) % services.length];
                                return (
                                    <g key={`lines-${s.id}`}>
                                        {/* Line to center */}
                                        <line
                                            className={`LD-center-line line-to-center-${s.id}`}
                                            x1="200" y1="200" x2={s.x} y2={s.y}
                                            strokeDasharray="500" strokeDashoffset="0"
                                        />
                                        {/* Outer pentagon line */}
                                        <line
                                            className={`LD-outer-line line-outer-${s.id}`}
                                            x1={s.x} y1={s.y} x2={next.x} y2={next.y}
                                            strokeDasharray="500" strokeDashoffset="0"
                                        />
                                    </g>
                                );
                            })}
                        </svg>

                        {/* Center Hub */}
                        <div className="LD-center-hub">
                            <div className="LD-hub-content">
                                <Cpu size={40} className="LD-hub-icon" />
                                <span className="LD-hub-label">CORE</span>
                            </div>
                            <div className="LD-hub-pulse"></div>
                        </div>

                        {/* Service Nodes */}
                        {services.map((service) => (
                            <div
                                key={service.id}
                                className={`LD-pentagon-node node-${service.id}`}
                                style={{ top: `${(service.y / 400) * 100}%`, left: `${(service.x / 400) * 100}%` }}
                            >
                                <div className="LD-node-wrapper">
                                    <div className="LD-node-icon">
                                        {service.icon}
                                    </div>
                                    <div className="LD-node-label">{service.name}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Skills() {
    const sectionRef = React.useRef(null);

    React.useEffect(() => {
        const cards = sectionRef.current.querySelectorAll(".LD-service-card");

        gsap.set(cards, {
            y: 0,
            opacity: 1,
            scale: 1
        });
        ScrollTrigger.refresh();
    }, []);

    return (
        <section id="services" className="CS-section" ref={sectionRef}>
            <div className="container">
                <div className="CS-header">
                    <span className="CS-badge">Core Services</span>
                    <h2 className="CS-title">What We Build</h2>
                    <p className="CS-subtitle">
                        Production-ready systems engineered for scale and performance
                    </p>
                </div>

                <div className="CS-bg-icon">
                    <Code size={400} />
                </div>

                <div className="LD-services-grid">
                    <Link to="/AI_Automation" className="LD-service-card" style={{ textDecoration: 'none' }}>
                        <div className="LD-service-icon">
                            <Settings size={32} strokeWidth={1.5} />
                        </div>
                        <h3 className="LD-service-title">AI Systems & Intelligent Automation</h3>
                        <p className="LD-service-desc">
                            Engineering autonomous agents and neural workflows that handle complex business logic with human-like precision.
                        </p>
                        <span className="LD-service-link">
                            Explore Automation →
                        </span>
                    </Link>

                    <Link to="/CustomSoftware" className="LD-service-card" style={{ textDecoration: 'none' }}>
                        <div className="LD-service-icon">
                            <TrendingUp size={32} strokeWidth={1.5} />
                        </div>
                        <h3 className="LD-service-title">Enterprise Software Architecture</h3>
                        <p className="LD-service-desc">
                            Building robust, scalable platforms and data-driven dashboards precisely engineered for operational excellence.
                        </p>
                        <span className="LD-service-link">
                            View Infrastructure →
                        </span>
                    </Link>

                    <Link to="/MarketingSystem" className="LD-service-card" style={{ textDecoration: 'none' }}>
                        <div className="LD-service-icon">
                            <Target size={32} strokeWidth={1.5} />
                        </div>
                        <h3 className="LD-service-title">Growth Engines & Performance Marketing</h3>
                        <p className="LD-service-desc">
                            Deployment of high-velocity conversion systems and creative automation designed to dominate digital markets.
                        </p>
                        <span className="LD-service-link">
                            Analyze Growth →
                        </span>
                    </Link>

                    <Link to="/Web&Conversion" className="LD-service-card" style={{ textDecoration: 'none' }}>
                        <div className="LD-service-icon">
                            <Lightbulb size={32} strokeWidth={1.5} />
                        </div>
                        <h3 className="LD-service-title">UI/UX & Conversion Architecture</h3>
                        <p className="LD-service-desc">
                            Architecting high-performance digital interfaces that blend aesthetics with technical superiority to drive results.
                        </p>
                        <span className="LD-service-link">
                            Review Design →
                        </span>
                    </Link>
                </div>
            </div>
        </section>
    );
}

// Unused Lab component removed

function Process() {
    const sectionRef = React.useRef(null);

    React.useEffect(() => {
        const cards = sectionRef.current.querySelectorAll(".SR-card");

        gsap.set(cards, { y: 0, opacity: 1 });
        ScrollTrigger.refresh();
    }, []);

    return (
        <section id="process" className="SR-section" ref={sectionRef}>
            <div className="container">
                <div className="SR-bg-icon">
                    <Cpu size={500} />
                </div>
                <div className="SR-header centered-header">
                    <span className="SR-badge">Strategic Roadmap</span>
                    <h2 className="SR-title">The Engineering Path to Scale</h2>
                </div>

                <div className="SR-cards">
                    <div className="SR-card">
                        <div className="SR-number">
                            <Search size={32} />
                        </div>
                        <h3 className="SR-card-title">Discovery & Evaluation</h3>
                        <p className="SR-card-desc">
                            Deep-dive into your operational architecture to identify high-leverage automation opportunities.
                        </p>
                    </div>
                    <div className="SR-card">
                        <div className="SR-number">
                            <Monitor size={32} />
                        </div>
                        <h3 className="SR-card-title">
                            Controlled Studio Access
                        </h3>
                        <p className="SR-card-desc">
                            High-fidelity sandbox prototyping where we stress-test systems before full-scale integration.
                        </p>
                    </div>
                    <div className="SR-card">
                        <div className="SR-number">
                            <Rocket size={32} />
                        </div>
                        <h3 className="SR-card-title">Licensing & Deployment</h3>
                        <p className="SR-card-desc">
                            Production-grade deployment with full technical governance and horizontal scalability.
                        </p>
                    </div>
                </div>

                <div className="LD-process-footer">
                    <div className="LD-process-footer-card">
                        <div className="LD-footer-card-inner">
                            <Zap className="LD-footer-icon" size={40} strokeWidth={1.5} />
                            <div className="LD-footer-content">
                                <p className="LD-footer-label">Efficiency First</p>
                                <h4 className="LD-footer-text">Precision Over Hype</h4>
                                <p className="LD-footer-subtext">Eliminating noise to build systems that deliver immediate operational ROI.</p>
                            </div>
                        </div>
                    </div>
                    <div className="LD-process-footer-card">
                        <div className="LD-footer-card-inner">
                            <Shield className="LD-footer-icon" size={40} strokeWidth={1.5} />
                            <div className="LD-footer-content">
                                <p className="LD-footer-label">Rigorous Vetting</p>
                                <h4 className="LD-footer-text">Built for Production</h4>
                                <p className="LD-footer-subtext">Every line of code and AI prompt is stress-tested for 24/7 reliability.</p>
                            </div>
                        </div>
                    </div>
                    <div className="LD-process-footer-card">
                        <div className="LD-footer-card-inner">
                            <Database className="LD-footer-icon" size={40} strokeWidth={1.5} />
                            <div className="LD-footer-content">
                                <p className="LD-footer-label">Architecture Support</p>
                                <h4 className="LD-footer-text">Strategic Lifelines</h4>
                                <p className="LD-footer-subtext">Direct technical partnership to ensure your infrastructure evolves with your scale.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}



function Operators() {
    const sectionRef = React.useRef(null);
    const marqueeRef = React.useRef(null);

    React.useEffect(() => {
        const items = sectionRef.current.querySelectorAll(".OE-feature-item");

        // Section entrance animation
        gsap.set(items, { y: 0, opacity: 1 });
        gsap.set(".OE-partners-heading, .LD-marquee-container", { opacity: 1, y: 0, scale: 1 });
        ScrollTrigger.refresh();

        // Continuous Marquee Logic
        const marquee = marqueeRef.current;
        if (marquee) {
            const scrollWidth = marquee.scrollWidth;

            gsap.to(marquee, {
                x: `-${scrollWidth / 2}`,
                duration: 30,
                repeat: -1,
                ease: "none",
                modifiers: {
                    x: gsap.utils.unitize((x) => parseFloat(x) % (scrollWidth / 2))
                }
            });
        }
    }, []);

    const partners = [
        "Northstar Digital",
        "Atlas Growth Studio",
        "Vertex Systems",
        "Horizon Solutions",
        "NovaStack",
        "FleetIQ"
    ];

    return (
        <section className="OE-section" ref={sectionRef}>
            <div className="container">
                <div className="OE-header">
                    <span className="OE-badge">Operational Excellence</span>
                    <h2 className="OE-title">
                        Engineered for High-Velocity Operators
                    </h2>
                </div>

                <div style={{ marginBottom: "8rem" }}>
                    <div className="OE-feature-item">
                        <div className="OE-feature-icon">
                            <Check size={28} strokeWidth={3} />
                        </div>
                        <span className="OE-feature-text">
                            Scaling Agencies looking for R&D-grade technical leverage without the overhead.
                        </span>
                    </div>

                    <div className="OE-feature-item">
                        <div className="OE-feature-icon">
                            <Check size={28} strokeWidth={3} />
                        </div>
                        <span className="OE-feature-text">
                            Enterprises requiring rapid deployment of production-ready AI systems.
                        </span>
                    </div>

                    <div className="OE-feature-item">
                        <div className="OE-feature-icon">
                            <Check size={28} strokeWidth={3} />
                        </div>
                        <span className="OE-feature-text">
                            Strategic Partners launching next-gen tools without infrastructure risk.
                        </span>
                    </div>
                </div>

                <div className="OE-partners-section">
                    <h2 className="OE-partners-heading">Trusted By Industry-Leading Partners</h2>

                    <div className="LD-marquee-wrapper">
                        <div className="LD-marquee-container" ref={marqueeRef}>
                            {/* Duplicate the list to create a seamless loop */}
                            {[...partners, ...partners].map((name, index) => (
                                <div key={index} className="LD-partner-item">
                                    <span className="LD-partner-dot"></span>
                                    <span className="LD-partner-name">{name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Testimonial() {
    const [current, setCurrent] = React.useState(0);
    const cardRef = React.useRef(null);
    const sectionRef = React.useRef(null);

    const testimonials = [
        {
            text: "The quality and speed of delivery exceeded our expectations. True enterprise-grade systems that handle our scale effortlessly.",
            author: "Michael Torres",
            role: "CTO",
            company: "Vertex Systems",
            gradient: "linear-gradient(135deg, #0a0e1a 0%, #1e293b 100%)",
            accent: "#3b82f6"
        },
        {
            text: "Their approach to automation transformed how we operate. Seamless integration and exceptional support throughout the deployment.",
            author: "Sarah Chen",
            role: "VP of Operations",
            company: "Atlas Growth Studio",
            gradient: "linear-gradient(135deg, #0a0e1a 0%, #1f2937 100%)",
            accent: "#8b5cf6"
        },
        {
            text: "Best investment we made for scaling. The systems just work, and the team understands complex enterprise infrastructure needs.",
            author: "James Rodriguez",
            role: "Founder",
            company: "NovaStack",
            gradient: "linear-gradient(135deg, #0a0e1a 0%, #0f172a 100%)",
            accent: "#06b6d4"
        }
    ];

    const total = testimonials.length;

    useEffect(() => {
        const interval = setInterval(() => {
            handleNext();
        }, 8000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current]);

    const animateSlide = (direction) => {
        const isMobile = window.innerWidth <= 768;
        const tl = gsap.timeline();

        // Fade out current
        tl.to(cardRef.current, {
            y: isMobile ? 10 : 20,
            opacity: 0,
            duration: isMobile ? 0.2 : 0.4,
            ease: "power2.in",
            onComplete: () => {
                if (direction === "next") {
                    setCurrent((prev) => (prev + 1) % total);
                } else {
                    setCurrent((prev) => (prev === 0 ? total - 1 : prev - 1));
                }
            }
        });

        // Fade in new
        tl.fromTo(cardRef.current,
            { y: isMobile ? -10 : -20, opacity: 0 },
            { y: 0, opacity: 1, duration: isMobile ? 0.3 : 0.6, ease: "power2.out" }
        );
    };

    const handleNext = () => animateSlide("next");
    const handlePrev = () => animateSlide("prev");

    return (
        <section
            className="LD-testimonial-section"
            ref={sectionRef}
            style={{ background: testimonials[current].gradient, transition: "background 1s ease-in-out" }}
        >
            {/* Animated background blobs */}
            <div className="LD-testi-blob LD-testi-blob-1" style={{ background: testimonials[current].accent }}></div>
            <div className="LD-testi-blob LD-testi-blob-2" style={{ background: testimonials[current].accent }}></div>

            <div className="container">
                <div className="LD-testimonial-header">
                    <div className="LD-testimonial-badge">
                        <Star size={24} fill="white" color="transparent" />
                    </div>
                    <p className="LD-testimonial-label">Enterprise Validation</p>
                    <h2 className="LD-testimonial-main-title">Trusted By High-Volume Operators</h2>
                </div>

                <div className="LD-testimonial-carousel">
                    <Quote className="LD-quote-icon top" size={60} />

                    <div className="LD-testimonial-card-container" ref={cardRef}>
                        <div className="LD-testimonial-card">
                            <p className="LD-testimonial-text">
                                {testimonials[current].text}
                            </p>
                            <div className="LD-testimonial-meta">
                                <div className="LD-author-info">
                                    <p className="LD-testimonial-author">{testimonials[current].author}</p>
                                    <p className="LD-testimonial-role">
                                        {testimonials[current].role} <span>@</span> {testimonials[current].company}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Quote className="LD-quote-icon bottom" size={60} />

                    <div className="LD-testimonial-controls">
                        <button className="LD-testimonial-nav-btn LD-prev" onClick={handlePrev}>
                            <ChevronLeft size={24} />
                        </button>
                        <div className="LD-testimonial-indicators-v2">
                            {testimonials.map((_, i) => (
                                <span
                                    key={i}
                                    className={`LD-testi-dot ${current === i ? "LD-active" : ""}`}
                                    onClick={() => {
                                        if (i !== current) animateSlide(i > current ? "next" : "prev");
                                    }}
                                />
                            ))}
                        </div>
                        <button className="LD-testimonial-nav-btn LD-next" onClick={handleNext}>
                            <ChevronRight size={24} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

function VerticalSolutions() {
    const sectionRef = useRef(null);

    useEffect(() => {
        const isMobile = window.innerWidth <= 768;
        gsap.fromTo(".VS-card",
            { y: isMobile ? 20 : 50, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: isMobile ? 0.4 : 0.8,
                stagger: isMobile ? 0.05 : 0.2,
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 80%",
                }
            }
        );
    }, []);

    const solutions = [
        {
            title: "Logistics & Supply Chain",
            desc: "Custom fleet management and automated dispatch systems for high-volume carriers.",
            icon: <Truck size={32} />,
            img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070&auto=format&fit=crop"
            // Cursor Prompt: Professional wide shot of a logistics warehouse with trucks and organized shelving, modern industrial look, bright lighting, high efficiency vibe.
        },
        {
            title: "Growth & Ad Agencies",
            desc: "Scaling operations with automated media buying scripts and real-time reporting dashboards.",
            icon: <TrendingUp size={32} />,
            img: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop"
            // Cursor Prompt: Collaborative team meeting in a modern glass office, diverse group of young professionals looking at a dashboard screen, bright aesthetics, creative agency feel.
        },
        {
            title: "Enterprise R&D",
            desc: "Specialized AI tools for data analysis and predictive modeling in regulated environments.",
            icon: <Settings size={32} />,
            img: "https://images.unsplash.com/photo-1531297484001-80022131f5a1?q=80&w=2020&auto=format&fit=crop"
            // Cursor Prompt: Futuristic technology circuit board with glowing light trails, macro shot, cinematic depth of field, high-tech engineering concept.
        }
    ];

    return (
        <section className="VS-section" ref={sectionRef}>
            <div className="container">
                <div className="VS-header">
                    <span className="VS-badge">Vertical Solutions</span>
                    <h2 className="VS-title">Built for Your Industry</h2>
                    <p className="VS-desc">We don't build generic software. We build systems that understand your specific operational bottlenecks.</p>
                </div>

                <div className="VS-grid">
                    {solutions.map((item, i) => (
                        <div key={i} className="VS-card">
                            <div className="VS-card-image-wrapper">
                                <img src={item.img} alt={item.title} className="VS-card-image" />
                                <div className="VS-card-overlay"></div>
                            </div>
                            <div className="VS-card-content">
                                <div className="VS-card-icon">{item.icon}</div>
                                <h3 className="VS-card-title">{item.title}</h3>
                                <p className="VS-card-text">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Call() {
    return (
        <section className="SD-section">
            <div className="CN-footer-grid-overlay"></div>
            <div className="container">
                <div className="SD-centered-content">
                    <div className="SD-bg-icon">
                        <Rocket size={400} />
                    </div>

                    <div className="SD-header-group">
                        <span className="SD-badge">Strategic Deployment</span>
                        <h2 className="SD-title">
                            Ready to Orchestrate <br />
                            <span className="SD-gradient-text">Your Digital Future?</span>
                        </h2>
                        <p className="SD-desc">
                            We don't sell templates or generic tools. We deploy production-ready systems
                            designed for high-performance operators and mission-critical workflows.
                        </p>
                    </div>

                    <div className="AT-cta-buttons-group d-flex flex-wrap justify-content-center gap-4">
                        <a
                            href="https://studio.digitaldreamworksagency.com/access"
                            className="btn btn-lg LD-btn-primary px-5 py-3 shadow-lg"
                            onClick={() => {
                                if (window.fbq) {
                                    window.fbq('trackCustom', 'RequestDemoOpened', {
                                        source: 'call_component'
                                    });
                                }
                            }}
                        >
                            <Sparkles size={20} className="me-2" />
                            Request System Access
                        </a>
                        <a
                            href="https://calendly.com/digi-dreamworks/onboarding-call"
                            className="btn btn-lg LD-btn-secondary px-5 py-3"
                        >
                            Book Commercial Call
                        </a>
                    </div>

                    <div className="CTA-trust-pills d-flex flex-wrap justify-content-center gap-3 mt-5">
                        <div className="CTA-trust-pill">
                            <Infinity size={14} className="text-primary" />
                            <span>Zero Infrastructure Risk</span>
                        </div>
                        <div className="CTA-trust-pill">
                            <BarChart3 size={14} className="text-primary" />
                            <span>Measurable ROI Logic</span>
                        </div>
                        <div className="CTA-trust-pill">
                            <Shield size={14} className="text-primary" />
                            <span>Enterprise-Grade Security</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Footer() {
    const [form, setForm] = React.useState({
        name: "",
        email: "",
        message: "",
        company: "", // honeypot
    });

    const [status, setStatus] = React.useState("");

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Honeypot check (bots)
        if (form.company) return;

        setStatus("Sending...");

        emailjs
            .send(
                "service_8knu1bd",
                "template_r4a706c",
                {
                    from_name: form.name,
                    from_email: form.email,
                    message: form.message,
                },
                "YT2VTVo7DBR7Ub75i"
            )
            .then(
                () => {
                    setStatus("Message sent successfully.");
                    setForm({
                        name: "",
                        email: "",
                        message: "",
                        company: "",
                    });
                },
                () => {
                    setStatus("Failed to send message. Try again later.");
                }
            );
    };

    return (
        <footer className="CN-footer">
            <div className="CN-footer-grid-overlay"></div>
            <div className="container">
                <div className="row">
                    {/* Brand & Contact */}
                    <div className="col-lg-5 col-md-12 mb-4">
                        <h3 className="CN-footer-brand">Digital Dream Works</h3>
                        <p className="CN-footer-description">
                            An AI and Software Studio building production-ready systems for agencies, enterprises, and strategic partners. Designed for real operations. Deployed for scale.
                        </p>

                        <div className="CN-footer-contact">
                            <div className="CN-footer-contact-item">
                                <MapPin size={18} className="CN-footer-icon" />
                                <span>
                                    Digital Dream Works LLC <br /> St. Petersburg, FL, USA
                                </span>
                            </div>
                            <div className="CN-footer-contact-item">
                                <Mail size={18} className="CN-footer-icon" />
                                <a href="mailto:hello@DigitalDreamWorksAgency.com">
                                    hello@DigitalDreamWorksAgency.com
                                </a>
                            </div>
                            <div className="CN-footer-contact-item">
                                <Phone size={18} className="CN-footer-icon" />
                                <a href="tel:+17867352988">+1 786 735 2988</a>
                            </div>
                        </div>

                        <div className="CN-footer-socials">
                            <a
                                href="https://www.linkedin.com/company/digital-dream-works"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="CN-footer-social-link"
                            >
                                <Linkedin size={20} />
                            </a>


                            <a
                                href="https://www.instagram.com/digi.dreamworks?igsh=MWVyZjI1dDhnYTYyNw=="
                                target="_blank"
                                rel="noopener noreferrer"
                                className="CN-footer-social-link"
                            >
                                <Instagram size={20} />
                            </a>
                        </div>
                    </div>

                    {/* Links */}
                    <div className="col-lg-2 col-md-6 mb-4">
                        <h4 className="CN-footer-title">Explore</h4>
                        <ul className="CN-footer-links-list">
                            <li>
                                <Link to="/AboutUs" className="CN-footer-link">
                                    <ArrowRight size={16} className="CN-link-arrow" /> About
                                </Link>
                            </li>
                            <li>
                                <Link to="/Labs" className="CN-footer-link">
                                    <ArrowRight size={16} className="CN-link-arrow" /> Labs
                                </Link>
                            </li>
                            <li>
                                <Link to="/PartnerShips" className="CN-footer-link">
                                    <ArrowRight size={16} className="CN-link-arrow" />{" "}
                                    Partnerships
                                </Link>
                            </li>
                            <li>
                                <Link to="/How_we_work" className="CN-footer-link">
                                    <ArrowRight size={16} className="CN-link-arrow" /> How We Work
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact Form */}
                    <div className="col-lg-5 col-md-12">
                        <h4 className="CN-footer-title">Enterprise Contact</h4>

                        <form className="CN-footer-form" onSubmit={handleSubmit}>
                            {/* Honeypot */}
                            <input
                                type="text"
                                name="company"
                                value={form.company}
                                onChange={handleChange}
                                style={{ display: "none" }}
                            />

                            <div className="CN-input-group">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Your Name"
                                    value={form.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="CN-input-group">
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Work Email"
                                    value={form.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="CN-input-group">
                                <textarea
                                    name="message"
                                    placeholder="How can we help?"
                                    rows="3"
                                    value={form.message}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <button type="submit" className="CN-submit-btn">
                                <span>Send Inquiry</span>
                                <Send size={16} />
                            </button>

                            {status && <p className="CN-footer-form-status">{status}</p>}
                        </form>
                    </div>
                </div>

                <div className="CN-footer-bottom">
                    <p style={{ marginBottom: "0.5rem", opacity: "0.7", fontSize: "0.9rem" }}>
                        Digital Dream Works builds operational AI systems for regulated industries, including healthcare.
                    </p>
                    <p>© 2026 Digital Dream Works. All rights reserved.</p>
                </div>
            </div>
        </footer>
    );
}

// ==========================================
// MAIN COMPONENT
// ==========================================

export default function LandingPage() {
    React.useEffect(() => {
        // Add specific class to body for homepage dark theme scoping
        document.body.classList.add("HomePage-body");

        const sections = gsap.utils.toArray(".animate-section-reveal");

        sections.forEach((section, i) => {
            const isMobile = window.innerWidth <= 768;
            // Target headings, paragraphs, and badges
            const targets = section.querySelectorAll("h1, h2, h3, h4, p, .LD-excellence-badge, .LD-approach-subtitle, .LD-labs-badge, .VS-badge, .LD-testimonial-label, .CTA-badge, .CompS-badge, .CS-badge, .SR-badge, .OE-badge, .SD-badge");

            const direction = i % 2 === 0 ? -150 : 150;

            gsap.fromTo(targets,
                { x: isMobile ? 0 : direction, opacity: 0, y: isMobile ? 20 : 0 },
                {
                    x: 0,
                    y: 0,
                    opacity: 1,
                    duration: isMobile ? 0.6 : 1.4,
                    stagger: isMobile ? 0.05 : 0.1,
                    ease: "expo.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 85%",
                        toggleActions: "play none none reverse"
                    }
                }
            );
        });

        // Add a smooth horizontal scroll containment
        gsap.set(".animate-section-reveal", { overflow: "hidden" });

        return () => {
            ScrollTrigger.getAll().forEach(t => t.kill());
            // Remove the class when leaving the homepage
            document.body.classList.remove("HomePage-body");
        };
    }, []);

    return (
        <div style={{ overflowX: "hidden" }}>
            <BackgroundAnimation />
            <Navbar />

            <div className="animate-section-reveal">
                <Hero />
            </div>

            <div className="animate-section-reveal">
                <Intro />
            </div>

            <div className="animate-section-reveal">
                <Skills />
            </div>

            <div className="animate-section-reveal">
                <Process />
            </div>

            <div className="animate-section-reveal">
                <Operators />
            </div>

            <div className="animate-section-reveal">
                <VerticalSolutions />
            </div>

            <div className="animate-section-reveal">
                <Testimonial />
            </div>

            <div className="animate-section-reveal">
                <Call />
            </div>

            <Footer />
            <ChatbotWidget />
        </div>
    );
}